import type { CellMark, Puzzle } from "../../shared/nonogram/types";

const GAME_PREFIX = "nonogram:v1:game:";
const STATS_KEY = "nonogram:v1:stats";
const VISIT_KEY = "nonogram:v1:last-visit";
const PLAY_MODE_KEY = "nonogram:v1:play-mode";
const ZOOM_KEY = "nonogram:v1:board-zoom";
const ZOOM_BY_SIZE_KEY = "nonogram:v1:board-zoom-by-size";
const COMPLETED_DATES_CAP = 90;

export type PlayMode = "challenge" | "relaxed";

export interface Snapshot {
  cells: CellMark[];
  mistakes: number;
}

export interface StoredGame {
  version: 2;
  cells: CellMark[];
  history: Snapshot[];
  elapsedSeconds: number;
  mistakes: number;
  started: boolean;
  completed: boolean;
}

export interface PlayerStats {
  version: 1;
  completed: number;
  bestTimes: Record<string, number>;
  dailyStreak: number;
  lastDailyDate?: string;
  /** UTC YYYY-MM-DD dates of completed daily puzzles, newest last, capped. */
  completedDailyDates: string[];
}

const EMPTY_STATS: PlayerStats = {
  version: 1,
  completed: 0,
  bestTimes: {},
  dailyStreak: 0,
  completedDailyDates: [],
};

function safeParse(value: string | null): unknown {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return undefined;
  }
}

function isCellMark(value: unknown): value is CellMark {
  return value === -1 || value === 0 || value === 1;
}

function isCellArray(value: unknown, expectedCells: number): value is CellMark[] {
  return Array.isArray(value) && value.length === expectedCells && value.every(isCellMark);
}

// Version 1 stored undo snapshots as bare cell arrays, before undo also restored the
// mistake count. Those games keep playing; their earlier moves just undo to zero.
function readSnapshot(value: unknown, expectedCells: number): Snapshot | undefined {
  if (isCellArray(value, expectedCells)) return { cells: value, mistakes: 0 };
  if (!value || typeof value !== "object") return undefined;
  const snapshot = value as Partial<Snapshot>;
  if (!isCellArray(snapshot.cells, expectedCells)) return undefined;
  return { cells: snapshot.cells, mistakes: typeof snapshot.mistakes === "number" ? Math.max(0, snapshot.mistakes) : 0 };
}

export function loadGame(sessionId: string, expectedCells: number): StoredGame | undefined {
  const raw = safeParse(localStorage.getItem(`${GAME_PREFIX}${sessionId}`));
  if (!raw || typeof raw !== "object") return undefined;
  const game = raw as Omit<Partial<StoredGame>, "version"> & { version?: number };
  if (
    (game.version !== 1 && game.version !== 2) ||
    !isCellArray(game.cells, expectedCells) ||
    !Array.isArray(game.history)
  ) {
    return undefined;
  }
  return {
    version: 2,
    cells: game.cells,
    history: game.history
      .map((snapshot) => readSnapshot(snapshot, expectedCells))
      .filter((snapshot): snapshot is Snapshot => snapshot !== undefined),
    elapsedSeconds: typeof game.elapsedSeconds === "number" ? Math.max(0, game.elapsedSeconds) : 0,
    mistakes: typeof game.mistakes === "number" ? Math.max(0, game.mistakes) : 0,
    started: game.started === true,
    completed: game.completed === true,
  };
}

export function saveGame(sessionId: string, game: StoredGame): void {
  try {
    localStorage.setItem(`${GAME_PREFIX}${sessionId}`, JSON.stringify(game));
  } catch {
    // Private browsing and storage quotas should not block play.
  }
}

export function clearGame(sessionId: string): void {
  try {
    localStorage.removeItem(`${GAME_PREFIX}${sessionId}`);
  } catch {
    // Storage is an enhancement, not a requirement for play.
  }
}

function normalizeCompletedDates(value: unknown, lastDailyDate?: string): string[] {
  const dates: string[] = [];
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (typeof entry === "string" && /^\d{4}-\d{2}-\d{2}$/.test(entry) && !dates.includes(entry)) {
        dates.push(entry);
      }
    }
  }
  if (lastDailyDate && !dates.includes(lastDailyDate)) dates.push(lastDailyDate);
  dates.sort();
  return dates.slice(-COMPLETED_DATES_CAP);
}

export function loadStats(): PlayerStats {
  const raw = safeParse(localStorage.getItem(STATS_KEY));
  if (!raw || typeof raw !== "object") return EMPTY_STATS;
  const stats = raw as Partial<PlayerStats>;
  if (stats.version !== 1) return EMPTY_STATS;
  const lastDailyDate = typeof stats.lastDailyDate === "string" ? stats.lastDailyDate : undefined;
  return {
    version: 1,
    completed: typeof stats.completed === "number" ? Math.max(0, stats.completed) : 0,
    bestTimes: stats.bestTimes && typeof stats.bestTimes === "object" ? stats.bestTimes : {},
    dailyStreak: typeof stats.dailyStreak === "number" ? Math.max(0, stats.dailyStreak) : 0,
    lastDailyDate,
    completedDailyDates: normalizeCompletedDates(stats.completedDailyDates, lastDailyDate),
  };
}

function dateDifference(left: string, right: string): number {
  return Math.round((Date.parse(`${left}T00:00:00Z`) - Date.parse(`${right}T00:00:00Z`)) / 86_400_000);
}

export function recordCompletion(puzzleId: string, seconds: number, dailyDate?: string): PlayerStats {
  const current = loadStats();
  const previousBest = current.bestTimes[puzzleId];
  const next: PlayerStats = {
    ...current,
    completed: current.completed + 1,
    bestTimes: {
      ...current.bestTimes,
      [puzzleId]: previousBest === undefined ? seconds : Math.min(previousBest, seconds),
    },
    completedDailyDates: [...current.completedDailyDates],
  };

  if (dailyDate && current.lastDailyDate !== dailyDate) {
    next.dailyStreak = current.lastDailyDate && dateDifference(dailyDate, current.lastDailyDate) === 1
      ? current.dailyStreak + 1
      : 1;
    next.lastDailyDate = dailyDate;
  }

  if (dailyDate && !next.completedDailyDates.includes(dailyDate)) {
    next.completedDailyDates = [...next.completedDailyDates, dailyDate].sort().slice(-COMPLETED_DATES_CAP);
  }

  try {
    localStorage.setItem(STATS_KEY, JSON.stringify(next));
  } catch {
    // Completion still counts in the current UI if storage is unavailable.
  }
  return next;
}

/**
 * Whether this browser was last here on an earlier day, latched for the session.
 *
 * The north-star metric counts players who came back *and* finished something, but
 * the events carry no identifier to join on — deliberately, because one would be a
 * device identifier and docs/MEASUREMENT_PLAN.md rules those out. So the browser,
 * which already knows the answer, states it on the completion event instead.
 */
let returningVisit = false;

/** The site's day boundary everywhere else is UTC, so it is the boundary here too. */
function utcDay(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

export function recordVisit(now = Date.now()): number | undefined {
  const previous = Number(localStorage.getItem(VISIT_KEY));
  try {
    localStorage.setItem(VISIT_KEY, String(now));
  } catch {
    return undefined;
  }
  if (!Number.isFinite(previous) || previous <= 0) return undefined;
  // Calendar day, not elapsed hours. The returned bucket counts whole 24-hour
  // periods, which is a different question and undercounts the commonest return of
  // all — played last night, came back this morning. That bucket is left alone
  // because it feeds an existing metric; this flag answers "was the player here on
  // an earlier day", which is what returning means to a daily puzzle.
  //
  // Latched rather than assigned: StrictMode runs the mount effect twice in
  // development, and the second call reads back the timestamp the first one just
  // wrote — which would report a returning player as brand new.
  if (utcDay(previous) !== utcDay(now)) returningVisit = true;
  return Math.floor((now - previous) / 86_400_000);
}

export function isReturningVisit(): boolean {
  return returningVisit;
}

// Play mode and zoom are preferences rather than progress: losing them costs a
// player one tap, so a failed write is not worth surfacing.
export function loadPlayMode(): PlayMode {
  const value = localStorage.getItem(PLAY_MODE_KEY);
  return value === "relaxed" ? "relaxed" : "challenge";
}

export function savePlayMode(mode: PlayMode): void {
  try {
    localStorage.setItem(PLAY_MODE_KEY, mode);
  } catch {
    // Preference is optional.
  }
}

export function loadBoardZoom(size?: Puzzle["size"]): number {
  // A zoom saved before sizes were remembered separately still applies, as the
  // starting point for any size that has no memory of its own yet.
  const fallback = readZoom(ZOOM_KEY);
  if (size === undefined) return fallback;
  const bySize = safeParse(localStorage.getItem(ZOOM_BY_SIZE_KEY));
  const value = bySize && typeof bySize === "object" ? (bySize as Record<string, unknown>)[String(size)] : undefined;
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(1.6, Math.max(0.7, Math.round(value * 100) / 100));
}

export function saveBoardZoom(size: Puzzle["size"], zoom: number): void {
  try {
    localStorage.setItem(ZOOM_KEY, String(zoom));
    const bySize = safeParse(localStorage.getItem(ZOOM_BY_SIZE_KEY)) ?? {};
    if (bySize && typeof bySize === "object" && !Array.isArray(bySize)) {
      (bySize as Record<string, number>)[String(size)] = zoom;
      localStorage.setItem(ZOOM_BY_SIZE_KEY, JSON.stringify(bySize));
    }
  } catch {
    // Preference is optional.
  }
}

function readZoom(key: string): number {
  const raw = localStorage.getItem(key);
  if (raw === null || raw === "") return 1;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 1;
  return Math.min(1.6, Math.max(0.7, Math.round(value * 100) / 100));
}

/** Board assists a player opts into. Off by default: the plain board is the
    product, and every assist is something a careful player can do by hand. */
export interface AssistSettings {
  autoCrossLines: boolean;
  tapCycles: boolean;
}

const ASSISTS_KEY = "nonogram:v1:assists";

export function loadAssistSettings(): AssistSettings {
  const raw = safeParse(localStorage.getItem(ASSISTS_KEY));
  if (!raw || typeof raw !== "object") return { autoCrossLines: false, tapCycles: false };
  const value = raw as Record<string, unknown>;
  return {
    autoCrossLines: value.autoCrossLines === true,
    tapCycles: value.tapCycles === true,
  };
}

export function saveAssistSettings(settings: AssistSettings): void {
  try {
    localStorage.setItem(ASSISTS_KEY, JSON.stringify(settings));
  } catch {
    // Preference is optional.
  }
}
